import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import CommonTable from "../component/CommonTable";
import MessageToster from "../component/MessageToster";
import axios from "axios";
import { BASE_URL } from "../api";
import { exportToExcel } from "../utils/exportData";
import Loader from "../component/Loader";
import { useHandleError } from "../hooks/useHandleError";
import { useRealAccounts } from "../hooks/useRealAccounts";
import { getApiHeaders } from "../utils/helpers";
import { formatDateDDMMYYYY } from "../utils/dateUtils";
import ExportButton from "../component/ExportButton";
import MobileFilter from "../component/MobileFilter";

// "YYYY-MM-DD" in local time, comparable with the <input type="date"> values
const toDateKey = (value) => {
  if (!value) return "";
  const date = new Date(String(value).split(".")[0]);
  if (isNaN(date)) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export default function AccountTransaction({ defaultTab }) {
  const handleError = useHandleError();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const typeParam = searchParams.get("type");

  // Tab State: "mt5" (Wallet to MT5 History) | "user" (User to User History)
  const [activeTab, setActiveTab] = useState(() => {
    if (defaultTab) return defaultTab;
    if (typeParam === "user") return "user";
    return "mt5";
  });

  useEffect(() => {
    if (typeParam === "user" && activeTab !== "user") {
      setActiveTab("user");
    } else if (typeParam === "mt5" && activeTab !== "mt5") {
      setActiveTab("mt5");
    }
  }, [typeParam]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ type: tab });
  };

  const { accounts, loadingAccounts } = useRealAccounts();
  const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"

  // ==========================================
  // 1. Wallet to MT5 Transfer History State
  // ==========================================
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    account: "",
    fromDate: "",
    toDate: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    const defaultAcc = accounts && accounts.length > 0 ? accounts[0] : "";
    const resetFilters = {
      account: defaultAcc,
      fromDate: "",
      toDate: "",
    };
    setSearchTerm("");
    setSearchQuery("");
    setFilters(resetFilters);
    if (defaultAcc) {
      fetchTransactions(resetFilters);
    } else {
      setTransactions([]);
    }
  };

  useEffect(() => {
    if (accounts && accounts.length > 0) {
      const savedAcc = sessionStorage.getItem("live_account_no") || sessionStorage.getItem("selected_account");
      const defaultAcc = savedAcc && accounts.includes(savedAcc) ? savedAcc : accounts[0];
      setFilters((prev) => ({ ...prev, account: defaultAcc }));
      fetchTransactions({
        account: defaultAcc,
        fromDate: filters.fromDate,
        toDate: filters.toDate,
      });
    }
  }, [accounts]);

  const fetchTransactions = async (customFilters = filters) => {
    if (!customFilters.account) {
      MessageToster("error", "Please select an account");
      setTransactions([]);
      return;
    }

    if (customFilters.fromDate && customFilters.toDate && new Date(customFilters.fromDate) > new Date(customFilters.toDate)) {
      MessageToster("error", "From date cannot be greater than To date");
      return;
    }

    const inputBody = {
      loginid: Number(customFilters.account),
      from_date: customFilters.fromDate || "2020-01-01",
      to_date: customFilters.toDate || today,
      transaction_type: "all",
    };

    setLoading(true);
    try {
      const res = await axios.post(`${BASE_URL}/MT5M/balance_history`, inputBody, {
        headers: getApiHeaders()
      });

      if (res.data?.code === 200) {
        setSearchQuery(searchTerm);
        const dataList = Array.isArray(res.data.data) ? res.data.data : [];
        setTransactions(dataList);
      } else {
        setTransactions([]);
        MessageToster("error", res.data?.message || "Failed to fetch transactions");
      }
    } catch (error) {
      setTransactions([]);
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    const exportData = filteredData.map((item, index) => ({
      "#": index + 1,
      "Date": item.ondate ? formatDateDDMMYYYY(item.ondate) : (item.time ? formatDateDDMMYYYY(new Date(item.time * 1000)) : "-"),
      "Account No": item.account_no || item.Account || "-",
      "Transfer ID": item.transferid || item.deal_id || "-",
      // Transaction Type, Charge, Payable and Status are not sent by the balance_history API - hidden for now
      // "Transaction Type": item.ttype || item.tran_type || "-",
      "Amount": item.amount != null ? Number(item.amount).toFixed(2) : "-",
      // "Charge": item.charge != null ? Number(item.charge).toFixed(2) : "0.00",
      // "Payable": item.payable != null ? Number(item.payable).toFixed(2) : "-",
      // "Status": item.status || item.istatus || "-",
      "Note": item.note || item.comment || "-"
    }));
    exportToExcel(exportData, "wallet_to_mt5_history", "default");
  };

  const columns = [
    {
      header: "Date",
      render: (row) => row.ondate ? formatDateDDMMYYYY(row.ondate) : (row.time ? formatDateDDMMYYYY(new Date(row.time * 1000)) : "-")
    },
    {
      header: "Account No",
      render: (row) => row.account_no || row.Account || "-"
    },
    {
      header: "Transfer ID",
      render: (row) => row.transferid || row.deal_id || "-"
    },
    // Transaction Type, Charge, Payable and Status are not sent by the balance_history API - hidden for now
    // {
    //   header: "Transaction Type",
    //   render: (row) => row.ttype || row.tran_type || "-"
    // },
    {
      header: "Amount",
      render: (row) => (
        <span className={`fw-semibold ${Number(row.amount) > 0 ? "text-success" : Number(row.amount) < 0 ? "text-danger" : ""}`}>
          {row.amount != null ? `${Number(row.amount) < 0 ? "-" : ""}$${Math.abs(Number(row.amount)).toFixed(2)}` : "-"}
        </span>
      )
    },
    // {
    //   header: "Charge",
    //   render: (row) => (
    //     <span className="text-muted">
    //       {row.charge != null ? `$${Number(row.charge).toFixed(2)}` : "$0.00"}
    //     </span>
    //   )
    // },
    // {
    //   header: "Payable",
    //   render: (row) => (
    //     <span className="fw-bold text-success">
    //       {row.payable != null ? `$${Number(row.payable).toFixed(2)}` : "-"}
    //     </span>
    //   )
    // },
    // {
    //   header: "Status",
    //   render: (row) => {
    //     const rawStatus = row.status || row.istatus || "-";
    //     const statusStr = String(rawStatus).toLowerCase();
    //     const isPending = ['pending', '0', 'processing'].includes(statusStr);
    //     const isSuccess = ['confirm', 'confirmed', 'success', 'completed', 'approve', 'approved', '1', 'done'].includes(statusStr);
    //     return (
    //       <span
    //         className={`badge fw-bold px-3 py-2 rounded-pill ${isPending
    //           ? "bg-warning bg-opacity-10 text-warning"
    //           : isSuccess
    //             ? "bg-success bg-opacity-10 text-success"
    //             : "bg-danger bg-opacity-10 text-danger"
    //           }`}
    //       >
    //         {rawStatus}
    //       </span>
    //     );
    //   }
    // },
    {
      header: "Note",
      render: (row) => row.note || row.comment || "-"
    }
  ];

  const filteredData = transactions.filter((item) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      String(item.account_no || item.Account || "").toLowerCase().includes(query) ||
      String(item.transferid || item.deal_id || "").toLowerCase().includes(query) ||
      String(item.ttype || item.tran_type || "").toLowerCase().includes(query) ||
      String(item.amount || "").toLowerCase().includes(query) ||
      String(item.status || item.istatus || "").toLowerCase().includes(query) ||
      String(item.note || item.comment || "").toLowerCase().includes(query)
    );
  });

  // ==========================================
  // 2. User to User Transfer History State
  // ==========================================
  const [userTransactions, setUserTransactions] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [selectedUserItem, setSelectedUserItem] = useState(null);
  const [userFilters, setUserFilters] = useState({
    fromDate: "",
    toDate: "",
  });
  // Date range of the last search. The API returns all rows regardless of from/to date,
  // so the table is also filtered by this range on the client.
  const [userAppliedDates, setUserAppliedDates] = useState({ fromDate: "", toDate: "" });

  const handleUserFilterChange = (e) => {
    const { name, value } = e.target;
    setUserFilters((prev) => ({ ...prev, [name]: value }));
  };

  const fetchUserTransactions = async (customFilters = userFilters) => {
    if (customFilters.fromDate && customFilters.toDate && new Date(customFilters.fromDate) > new Date(customFilters.toDate)) {
      MessageToster("error", "From date cannot be greater than To date");
      return;
    }

    setUserLoading(true);
    try {
      const body = {
        Transaction_Type: "Internal Transfer",
        from_date: customFilters.fromDate || "",
        to_date: customFilters.toDate || "",
      };
      const res = await axios.post(`${BASE_URL}/account/trasaction_history`, body, {
        headers: getApiHeaders()
      });

      if (res.data?.code === 200 || res.data?.status === "success" || res.data?.status === true) {
        setUserSearchQuery(userSearchTerm);
        setUserAppliedDates({ fromDate: customFilters.fromDate || "", toDate: customFilters.toDate || "" });
        const dataList = Array.isArray(res.data.data) ? res.data.data : [];
        setUserTransactions(dataList);
      } else {
        setUserTransactions([]);
        MessageToster("error", res.data?.message || "Failed to fetch user transfer history");
      }
    } catch (error) {
      setUserTransactions([]);
      handleError(error);
    } finally {
      setUserLoading(false);
    }
  };

  const clearUserFilters = () => {
    const resetFilters = {
      fromDate: "",
      toDate: "",
    };
    setUserSearchTerm("");
    setUserSearchQuery("");
    setUserFilters(resetFilters);
    fetchUserTransactions(resetFilters);
  };

  useEffect(() => {
    if (activeTab === "user") {
      fetchUserTransactions(userFilters);
    }
  }, [activeTab]);

  const handleUserExport = () => {
    const exportData = filteredUserData.map((item, index) => ({
      "#": index + 1,
      "Date": item.idate ? formatDateDDMMYYYY(item.idate) : "-",
      "Login ID": item.loginid || "-",
      "Receiver Email": item.email || "-",
      "Amount": item.amount != null ? Number(item.amount).toFixed(2) : "-",
      "Transfer Type": item.tran_type || item.ttype || "Internal Transfer",
      "Status": item.istatus || item.status || "-",
      "Remarks": item.remarks || "-"
    }));
    exportToExcel(exportData, "user_to_user_history", "default");
  };

  const filteredUserData = userTransactions.filter((item) => {
    const { fromDate, toDate } = userAppliedDates;
    if (fromDate || toDate) {
      const itemDate = toDateKey(item.idate);
      if (!itemDate) return false;
      if (fromDate && itemDate < fromDate) return false;
      if (toDate && itemDate > toDate) return false;
    }

    if (!userSearchQuery) return true;
    const query = userSearchQuery.toLowerCase();
    const idate = item.idate ? formatDateDDMMYYYY(item.idate) : "";
    const loginid = String(item.loginid || "").toLowerCase();
    const email = String(item.email || "").toLowerCase();
    const amount = item.amount != null ? String(item.amount).toLowerCase() : "";
    const type = String(item.tran_type || item.ttype || "").toLowerCase();
    const status = String(item.istatus || item.status || "").toLowerCase();
    const remarks = String(item.remarks || "").toLowerCase();

    return (
      idate.includes(query) ||
      loginid.includes(query) ||
      email.includes(query) ||
      amount.includes(query) ||
      type.includes(query) ||
      status.includes(query) ||
      remarks.includes(query)
    );
  });

  const userColumns = [
    {
      header: "Date",
      render: (row) => row.idate ? formatDateDDMMYYYY(row.idate) : "-"
    },
    {
      header: "Login ID",
      accessor: "loginid"
    },
    {
      header: "Receiver Email",
      accessor: "email"
    },
    {
      header: "Amount",
      render: (row) => (
        <span className="fw-semibold text-primary">
          {row.amount != null ? `$${Number(row.amount).toFixed(2)}` : "-"}
        </span>
      )
    },
    {
      header: "Transfer Type",
      render: (row) => row.tran_type || row.ttype || "Internal Transfer"
    },
    {
      header: "Status",
      render: (row) => {
        const rawStatus = row.istatus || row.status || "-";
        const statusStr = String(rawStatus).toLowerCase();
        const isPending = ['pending', '0', 'processing'].includes(statusStr);
        const isSuccess = ['confirm', 'confirmed', 'success', 'completed', 'approve', 'approved', '1', 'done'].includes(statusStr);
        return (
          <span
            className={`badge fw-bold px-3 py-2 rounded-pill ${isPending
              ? "bg-warning bg-opacity-10 text-warning"
              : isSuccess
                ? "bg-success bg-opacity-10 text-success"
                : "bg-danger bg-opacity-10 text-danger"
              }`}
          >
            {rawStatus}
          </span>
        );
      }
    },
    {
      header: "Remarks",
      render: (row) => row.remarks || "-"
    },
    {
      header: "Action",
      render: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-soft-primary"
          title="View Details"
          onClick={() => setSelectedUserItem(row)}
        >
          <i className="mdi mdi-eye-outline fs-5" />
        </button>
      )
    }
  ];

  return (
    <>
      <div id="layout-wrapper">
        {(loading || userLoading) && <Loader />}
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              {/* Page Title Row */}
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <h2 className="fw-700 mb-sm-0">
                      {activeTab === "user" ? "User to User History" : "Wallet to MT5 History"}
                    </h2>
                    <div className="page-title-right d-flex flex-wrap align-items-center gap-2 mt-2 mt-sm-0">
                      {/* Tab buttons with theme primary active and theme gray inactive backgrounds */}
                      <div className="d-flex align-items-center gap-2" role="tablist">
                        <button
                          type="button"
                          className="btn py-2 px-3 fw-semibold d-flex align-items-center gap-2 rounded-3 shadow-sm"
                          style={{
                            backgroundColor: activeTab === "mt5" ? "var(--color-primary)" : "#eaedf1",
                            color: activeTab === "mt5" ? "#ffffff" : "var(--color-text)",
                            border: `1px solid ${activeTab === "mt5" ? "var(--color-primary)" : "#d8dce2"}`,
                            transition: "all 0.2s ease-in-out",
                          }}
                          onClick={() => handleTabChange("mt5")}
                        >
                          <i className="bx bx-transfer-alt fs-5"></i>
                          <span>Wallet to MT5 History</span>
                        </button>
                        <button
                          type="button"
                          className="btn py-2 px-3 fw-semibold d-flex align-items-center gap-2 rounded-3 shadow-sm"
                          style={{
                            backgroundColor: activeTab === "user" ? "var(--color-primary)" : "#eaedf1",
                            color: activeTab === "user" ? "#ffffff" : "var(--color-text)",
                            border: `1px solid ${activeTab === "user" ? "var(--color-primary)" : "#d8dce2"}`,
                            transition: "all 0.2s ease-in-out",
                          }}
                          onClick={() => handleTabChange("user")}
                        >
                          <i className="bx bx-user-check fs-5"></i>
                          <span>User to User History</span>
                        </button>
                      </div>

                      <Link
                        to="/transfer"
                        className="btn btn-primary waves-effect waves-light d-flex align-items-center shadow-sm py-2 px-3"
                      >
                        <i className="bx bx-transfer-alt me-2 fs-5"></i> <span>Transfer Funds</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              {/* Main Card */}
              <div className="row">
                <div className="col-12">
                  <div className="card shadow-sm border-0">
                    <div className="card-body">

                      {/* =======================================================
                          TAB 1: Wallet to MT5 History
                          ======================================================= */}
                      {activeTab === "mt5" && (
                        <>
                          <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                            <div>
                              <label htmlFor="fromDateInput">From</label>
                              <input
                                id="fromDateInput"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="fromDate"
                                max={filters.toDate || today}
                                value={filters.fromDate}
                                onChange={handleChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="toDateInput">To</label>
                              <input
                                id="toDateInput"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="toDate"
                                max={today}
                                min={filters.fromDate || undefined}
                                value={filters.toDate}
                                onChange={handleChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="accountSelect">Account No</label>
                              <select
                                id="accountSelect"
                                className="form-select"
                                name="account"
                                value={filters.account}
                                onChange={handleChange}
                              >
                                <option value="" disabled hidden>
                                  {loadingAccounts
                                    ? "Loading accounts..."
                                    : accounts && accounts.length > 0
                                      ? "Select Account"
                                      : "No Account Found"}
                                </option>
                                {accounts.map((acc) => (
                                  <option key={acc} value={acc}>
                                    {acc}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label htmlFor="searchTermInput">Search</label>
                              <input
                                id="searchTermInput"
                                className="form-control"
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light"
                                onClick={() => fetchTransactions(filters)}
                                disabled={loading || !filters.account}
                              >
                                Search
                              </button>
                            </div>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light"
                                onClick={clearFilters}
                                disabled={!filters.fromDate && !filters.toDate && !searchTerm}
                              >
                                Clear
                              </button>
                            </div>
                            <div className="ms-auto">
                              <ExportButton
                                disabled={!filteredData || filteredData.length === 0}
                                onClick={handleExport}
                              />
                            </div>
                          </div>

                          <MobileFilter
                            title="Filter History"
                            onExport={handleExport}
                            exportDisabled={!filteredData || filteredData.length === 0}
                          >
                            <div>
                              <label htmlFor="fromDateInputMobile">From</label>
                              <input
                                id="fromDateInputMobile"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="fromDate"
                                max={filters.toDate || today}
                                value={filters.fromDate}
                                onChange={handleChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="toDateInputMobile">To</label>
                              <input
                                id="toDateInputMobile"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="toDate"
                                max={today}
                                min={filters.fromDate || undefined}
                                value={filters.toDate}
                                onChange={handleChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="accountSelectMobile">Account No</label>
                              <select
                                id="accountSelectMobile"
                                className="form-select"
                                name="account"
                                value={filters.account}
                                onChange={handleChange}
                              >
                                <option value="" disabled hidden>
                                  {loadingAccounts
                                    ? "Loading accounts..."
                                    : accounts && accounts.length > 0
                                      ? "Select Account"
                                      : "No Account Found"}
                                </option>
                                {accounts.map((acc) => (
                                  <option key={acc} value={acc}>
                                    {acc}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label htmlFor="searchMobile">Search</label>
                              <input
                                id="searchMobile"
                                className="form-control"
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                              />
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light flex-grow-1"
                                onClick={() => fetchTransactions(filters)}
                                disabled={loading || !filters.account}
                                data-bs-dismiss="offcanvas"
                                data-bs-target="#filterOffcanvasMobile"
                              >
                                Search
                              </button>
                              <button
                                type="button"
                                className="btn btn-light waves-effect waves-light border flex-grow-1"
                                onClick={clearFilters}
                                disabled={!filters.fromDate && !filters.toDate && !searchTerm}
                                data-bs-dismiss="offcanvas"
                                data-bs-target="#filterOffcanvasMobile"
                              >
                                Clear
                              </button>
                            </div>
                          </MobileFilter>

                          <CommonTable
                            columns={columns}
                            data={filteredData}
                            loading={loading}
                          />
                        </>
                      )}

                      {/* =======================================================
                          TAB 2: User to User Transfer History
                          ======================================================= */}
                      {activeTab === "user" && (
                        <>
                          <div className="d-flex flex-wrap align-items-end gap-2 mb-4 desktop-filter-row">
                            <div>
                              <label htmlFor="userFromDate">From</label>
                              <input
                                id="userFromDate"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="fromDate"
                                max={userFilters.toDate || today}
                                value={userFilters.fromDate}
                                onChange={handleUserFilterChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="userToDate">To</label>
                              <input
                                id="userToDate"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="toDate"
                                max={today}
                                min={userFilters.fromDate || undefined}
                                value={userFilters.toDate}
                                onChange={handleUserFilterChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="userSearchInput">Search</label>
                              <input
                                id="userSearchInput"
                                className="form-control"
                                type="text"
                                placeholder="Search email, ID, status..."
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                              />
                            </div>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light"
                                onClick={() => fetchUserTransactions(userFilters)}
                                disabled={userLoading}
                              >
                                Search
                              </button>
                            </div>
                            <div>
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light"
                                onClick={clearUserFilters}
                                disabled={!userFilters.fromDate && !userFilters.toDate && !userSearchTerm}
                              >
                                Clear
                              </button>
                            </div>
                            <div className="ms-auto">
                              <ExportButton
                                disabled={!filteredUserData || filteredUserData.length === 0}
                                onClick={handleUserExport}
                              />
                            </div>
                          </div>

                          <MobileFilter
                            title="Filter History"
                            onExport={handleUserExport}
                            exportDisabled={!filteredUserData || filteredUserData.length === 0}
                          >
                            <div>
                              <label htmlFor="userFromDateMobile">From</label>
                              <input
                                id="userFromDateMobile"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="fromDate"
                                max={userFilters.toDate || today}
                                value={userFilters.fromDate}
                                onChange={handleUserFilterChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="userToDateMobile">To</label>
                              <input
                                id="userToDateMobile"
                                className="form-control"
                                type="date"
                                onKeyDown={(e) => e.preventDefault()}
                                name="toDate"
                                max={today}
                                min={userFilters.fromDate || undefined}
                                value={userFilters.toDate}
                                onChange={handleUserFilterChange}
                              />
                            </div>
                            <div>
                              <label htmlFor="userSearchMobile">Search</label>
                              <input
                                id="userSearchMobile"
                                className="form-control"
                                type="text"
                                placeholder="Search..."
                                value={userSearchTerm}
                                onChange={(e) => setUserSearchTerm(e.target.value)}
                              />
                            </div>
                            <div className="d-flex gap-2 mt-2">
                              <button
                                type="button"
                                className="btn btn-primary waves-effect waves-light flex-grow-1"
                                onClick={() => fetchUserTransactions(userFilters)}
                                disabled={userLoading}
                                data-bs-dismiss="offcanvas"
                                data-bs-target="#filterOffcanvasMobile"
                              >
                                Search
                              </button>
                              <button
                                type="button"
                                className="btn btn-light waves-effect waves-light border flex-grow-1"
                                onClick={clearUserFilters}
                                disabled={!userFilters.fromDate && !userFilters.toDate && !userSearchTerm}
                                data-bs-dismiss="offcanvas"
                                data-bs-target="#filterOffcanvasMobile"
                              >
                                Clear
                              </button>
                            </div>
                          </MobileFilter>

                          <CommonTable
                            columns={userColumns}
                            data={filteredUserData}
                            loading={userLoading}
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* User to User Transfer Details Modal */}
          {selectedUserItem && (
            <div
              className="modal fade show"
              style={{ display: "block", backgroundColor: "rgba(0, 0, 0, 0.5)" }}
              tabIndex={-1}
              role="dialog"
              onClick={() => setSelectedUserItem(null)}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title d-flex align-items-center gap-2">
                      <i className="bx bx-detail fs-4"></i> Transfer Details
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      aria-label="Close"
                      onClick={() => setSelectedUserItem(null)}
                    ></button>
                  </div>
                  <div className="modal-body p-4">
                    <div className="transaction-details">
                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fw-semibold">Login ID</span>
                        <span className="fw-bold">{selectedUserItem.loginid || "-"}</span>
                      </div>

                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fw-semibold">Receiver Email</span>
                        <span className="fw-semibold text-break">{selectedUserItem.email || "-"}</span>
                      </div>

                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fw-semibold">Amount</span>
                        <span className="fw-bold fs-5 text-primary">
                          {selectedUserItem.amount != null
                            ? `$${Number(selectedUserItem.amount).toFixed(2)}`
                            : "-"}
                        </span>
                      </div>

                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fw-semibold">Transfer Type</span>
                        <span>{selectedUserItem.tran_type || selectedUserItem.ttype || "Internal Transfer"}</span>
                      </div>

                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fw-semibold">Date</span>
                        <span>{selectedUserItem.idate ? formatDateDDMMYYYY(selectedUserItem.idate) : "-"}</span>
                      </div>

                      <div className="d-flex justify-content-between py-2 border-bottom">
                        <span className="text-muted fw-semibold">Status</span>
                        {(() => {
                          const rawStatus = selectedUserItem.istatus || selectedUserItem.status || "-";
                          const statusStr = String(rawStatus).toLowerCase();
                          const isPending = ['pending', '0', 'processing'].includes(statusStr);
                          const isSuccess = ['confirm', 'confirmed', 'success', 'completed', 'approve', 'approved', '1', 'done'].includes(statusStr);
                          return (
                            <span
                              className={`badge fw-bold px-3 py-2 rounded-pill ${isPending
                                ? "bg-warning bg-opacity-10 text-warning"
                                : isSuccess
                                  ? "bg-success bg-opacity-10 text-success"
                                  : "bg-danger bg-opacity-10 text-danger"
                                }`}
                            >
                              {rawStatus}
                            </span>
                          );
                        })()}
                      </div>

                      <div className="d-flex justify-content-between py-2">
                        <span className="text-muted fw-semibold">Remarks</span>
                        <span>{selectedUserItem.remarks || "-"}</span>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer border-top-0 pt-0">
                    <button
                      type="button"
                      className="btn btn-secondary w-100 rounded-3"
                      onClick={() => setSelectedUserItem(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <Footer />
        </div>
      </div>
    </>
  );
}
